import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ReactComponent as InfoTooltipIcon } from '@assets/images/Input/info-tooltip.svg'
import { ReactComponent as SelectCheckIcon } from '@assets/images/Icons/Select_check.svg'
import Checkbox from '../Checkbox/Checkbox.component'
import type { SelectOption, SelectProps } from './Select.types'
import {
  SELECT_DEMO_ERROR_MESSAGE,
  SELECT_DEMO_FILLED_VALUE,
  SELECT_DEMO_INFO_MESSAGE,
  SELECT_DEMO_LABEL,
  SELECT_DEMO_MULTIPLE_VALUES,
  SELECT_DEMO_OPTIONS,
  SELECT_DEMO_PLACEHOLDER,
} from './Select.types'
import './Select.css'

const getOptionLabel = (options: SelectOption[], optionValue: string): string =>
  options.find((option) => option.value === optionValue)?.label ?? optionValue

const Select: React.FC<SelectProps> = ({
  label,
  placeholder = SELECT_DEMO_PLACEHOLDER,
  options = SELECT_DEMO_OPTIONS,
  value,
  defaultValue,
  multiple = false,
  disabled = false,
  error = false,
  helperText,
  optional = false,
  showTooltip = false,
  leadingIcon,
  className = '',
  darkMode = false,
  previewState,
  onChange,
}) => {
  const triggerId = useId()
  const listboxId = useId()
  const fieldRef = useRef<HTMLDivElement>(null)
  const [internalValue, setInternalValue] = useState<string | string[]>(() => {
    if (defaultValue != null) {
      return defaultValue
    }

    return multiple ? [] : ''
  })
  const [menuOpen, setMenuOpen] = useState(false)

  const isPreview = previewState != null
  const resolvedLabel = label ?? (isPreview ? SELECT_DEMO_LABEL : undefined)
  const isDisabled = disabled || previewState === 'disabled'
  const isError = error || previewState === 'error'
  const isOptional = optional || previewState === 'optional'
  const hasTooltip = showTooltip || previewState === 'with-tooltip'
  const hasLeadingIcon = leadingIcon === 'clock' || previewState === 'with-icon'

  const resolvedValue = useMemo(() => {
    if (isPreview) {
      if (previewState === 'multiple-options') {
        return SELECT_DEMO_MULTIPLE_VALUES
      }

      if (
        previewState === 'filled' ||
        previewState === 'disabled' ||
        previewState === 'error' ||
        previewState === 'with-info' ||
        previewState === 'expanded'
      ) {
        return SELECT_DEMO_FILLED_VALUE
      }

      return multiple ? [] : ''
    }

    return value ?? internalValue
  }, [internalValue, isPreview, multiple, previewState, value])

  const selectedValues = useMemo(
    () => (Array.isArray(resolvedValue) ? resolvedValue : []),
    [resolvedValue],
  )

  const hasSelection = multiple
    ? selectedValues.length > 0
    : Boolean(resolvedValue)

  const displayValue = useMemo(() => {
    if (multiple) {
      return selectedValues
        .map((optionValue) => getOptionLabel(options, optionValue))
        .join(', ')
    }

    return resolvedValue ? getOptionLabel(options, String(resolvedValue)) : ''
  }, [multiple, options, resolvedValue, selectedValues])

  const resolvedHelperText = useMemo(() => {
    if (helperText != null) {
      return helperText
    }

    if (previewState === 'error') {
      return SELECT_DEMO_ERROR_MESSAGE
    }

    if (previewState === 'with-info') {
      return SELECT_DEMO_INFO_MESSAGE
    }

    return undefined
  }, [helperText, previewState])

  const isMenuVisible =
    isPreview &&
    (previewState === 'expanded' || previewState === 'multiple-options')
      ? true
      : menuOpen

  const isOpenTrigger =
    isMenuVisible &&
    (previewState === 'expanded' ||
      previewState === 'multiple-options' ||
      (!isPreview && menuOpen))

  const emitChange = useCallback(
    (nextValue: string | string[]) => {
      if (value === undefined) {
        setInternalValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [onChange, value],
  )

  const handleToggleMenu = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen((open) => !open)
  }, [isDisabled, isPreview])

  const handleSelectOption = useCallback(
    (optionValue: string) => {
      if (multiple) {
        const nextValues = selectedValues.includes(optionValue)
          ? selectedValues.filter((item) => item !== optionValue)
          : [...selectedValues, optionValue]

        emitChange(nextValues)
        return
      }

      emitChange(optionValue)
      setMenuOpen(false)
    },
    [emitChange, multiple, selectedValues],
  )

  useEffect(() => {
    if (isPreview || !menuOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [isPreview, menuOpen])

  const classNames = [
    'ds-select',
    isDisabled ? 'ds-select--disabled' : '',
    isError ? 'ds-select--error' : '',
    darkMode ? 'ds-select--dark' : '',
    isPreview ? 'ds-select--preview' : '',
    previewState ? `ds-select--preview-${previewState}` : '',
    hasSelection ? 'ds-select--filled' : '',
    isOpenTrigger ? 'ds-select--open' : '',
    multiple ? 'ds-select--multiple' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const renderLeadingIcon = (): React.ReactNode => {
    if (!hasLeadingIcon) {
      return null
    }

    return (
      <i
        className="mgc mgc_time_line ds-select__leading-icon"
        aria-hidden="true"
      />
    )
  }

  const renderTriggerContent = (): React.ReactNode => (
    <>
      {renderLeadingIcon()}
      <span
        className={`ds-select__value${
          hasSelection ? ' ds-select__value--selected' : ''
        }`}
      >
        {hasSelection ? displayValue : placeholder}
      </span>
    </>
  )

  const renderOption = (option: SelectOption): React.ReactNode => {
    const isSelected = multiple
      ? selectedValues.includes(option.value)
      : resolvedValue === option.value

    if (multiple || previewState === 'multiple-options') {
      const isChecked =
        previewState === 'multiple-options'
          ? SELECT_DEMO_MULTIPLE_VALUES.includes(option.value)
          : isSelected

      return (
        <div
          key={option.value}
          className="ds-select__option ds-select__option--multiple"
        >
          <Checkbox
            checked={isChecked}
            label={option.label}
            darkMode={darkMode}
            disabled={isPreview || isDisabled}
            onChange={() => handleSelectOption(option.value)}
          />
        </div>
      )
    }

    return (
      <button
        key={option.value}
        type="button"
        className={`ds-select__option${
          isSelected ? ' ds-select__option--selected' : ''
        }`}
        role="option"
        aria-selected={isSelected}
        disabled={isPreview || isDisabled}
        onClick={() => handleSelectOption(option.value)}
      >
        {isSelected ? (
          <SelectCheckIcon
            className="ds-select__option-check"
            width={16}
            height={16}
            aria-hidden="true"
          />
        ) : null}
        <span className="ds-select__option-label">{option.label}</span>
      </button>
    )
  }

  const renderControl = (): React.ReactNode => (
    <div className="ds-select__control">
      <button
        id={triggerId}
        type="button"
        className="ds-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={isMenuVisible}
        aria-controls={listboxId}
        disabled={isPreview || isDisabled}
        tabIndex={isPreview ? -1 : 0}
        onClick={handleToggleMenu}
      >
        <span className="ds-select__trigger-content">
          {renderTriggerContent()}
        </span>
        <i
          className="mgc mgc_down_small_fill ds-select__chevron"
          aria-hidden="true"
        />
      </button>
    </div>
  )

  return (
    <div className={classNames}>
      {resolvedLabel ? (
        <div className="ds-select__label-row">
          <label className="ds-select__label" htmlFor={triggerId}>
            {resolvedLabel}
            {isOptional ? (
              <span className="ds-select__optional"> (Optional)</span>
            ) : null}
          </label>
          {hasTooltip ? (
            <span
              className="ds-select__tooltip-trigger"
              title={resolvedLabel}
              aria-label={`More information about ${resolvedLabel}`}
            >
              <InfoTooltipIcon />
            </span>
          ) : null}
        </div>
      ) : null}

      <div
        className={`ds-select__field${
          previewState === 'focused' ? ' ds-select__field--focused-preview' : ''
        }`}
        ref={fieldRef}
      >
        {renderControl()}

        {isMenuVisible ? (
          <div
            id={listboxId}
            className="ds-select__menu"
            role="listbox"
            aria-multiselectable={
              multiple || previewState === 'multiple-options'
            }
          >
            <div className="ds-select__menu-scroll">
              {options.map((option) => renderOption(option))}
            </div>
          </div>
        ) : null}
      </div>

      {resolvedHelperText ? (
        <p
          className={`ds-select__helper${
            isError ? ' ds-select__helper--error' : ''
          }`}
        >
          {resolvedHelperText}
        </p>
      ) : null}
    </div>
  )
}

export default Select
